import type { ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { isPartsRole } from "../../../../shared/domain.js";
import { useCaseCounts } from "../../../api/caseQueries.js";
import { useSession } from "../../../app/AppProviders.js";
import { roleHomePath } from "../../../app/RoleHomeRedirect.js";
import type { PartsPageDestination, PartsView } from "../presentation.js";
import { PartsSidebar, viewPath } from "./PartsSidebar.js";
import { parseWorkspaceUrlState, type WorkspaceUrlState, type WorkspaceUrlStateApi } from "./useWorkspaceUrlState.js";
import { WorkspaceFrame } from "./WorkspaceFrame.js";

/**
 * The service side's shell (spec §10; wholesale spec §4.1 — this is the
 * Service side of the switch): the shared {@link WorkspaceFrame} with the
 * Parts sidebar and its counts, framing the workspace, the order form and
 * the pages both sides share when they are opened from here. It carries
 * the role gate — the workspace is Parts' own: any other role that lands
 * here is sent to the workspace its role starts on — and, through the
 * frame, remembers the side and the place for the switch.
 *
 * The `WholesaleShell` is its twin on the other side.
 */

const PAGE_STATE: WorkspaceUrlState = parseWorkspaceUrlState(new URLSearchParams());

export interface PartsShellProps {
  /** The workspace passes its URL-state API; the pages leave it out and get plain links. */
  url?: WorkspaceUrlStateApi;
  activeDestination?: PartsView | PartsPageDestination;
  /** `workspace--page` for a single scrolling column (form, catalog, report). */
  page?: boolean;
  className?: string;
  /** Class for the top bar, the sidebar and the scrim (the report keeps them off paper). */
  chromeClassName?: string;
  mainClassName?: string;
  children: ReactNode;
}

export function PartsShell({ url, activeDestination, page = false, className, chromeClassName, mainClassName, children }: PartsShellProps) {
  const { user } = useSession();
  const navigate = useNavigate();
  const counts = useCaseCounts();

  if (user && !isPartsRole(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }

  const state = url?.state ?? PAGE_STATE;

  return (
    <WorkspaceFrame
      side="service"
      page={page}
      {...(className !== undefined ? { className } : {})}
      {...(chromeClassName !== undefined ? { chromeClassName } : {})}
      {...(mainClassName !== undefined ? { mainClassName } : {})}
      sidebar={(menu) => (
        <PartsSidebar
          id={menu.id}
          open={menu.open}
          state={state}
          counts={counts.data}
          {...(activeDestination !== undefined ? { activeDestination } : {})}
          onSelectView={(view) => (url ? url.setView(view) : navigate(viewPath(state, view)))}
          onNavigate={menu.onNavigate}
          {...(chromeClassName !== undefined ? { className: chromeClassName } : {})}
        />
      )}
    >
      {children}
    </WorkspaceFrame>
  );
}
